import { supabase } from '../supabase/client';
import { normalizeApiError, logApiError } from './errorHandler';

// Generic type for API requests
export interface ApiRequest<T> {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: T;
  params?: Record<string, unknown>;
}

// Generic type for API responses
export interface ApiResponse<T> {
  data: T | null;
  error: Error | null;
  status: number;
}

// API client function for Supabase
export const apiClient = async <TRequest, TResponse>(
  request: ApiRequest<TRequest>
): Promise<ApiResponse<TResponse>> => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    let response;
    
    switch (request.method) {
      case 'GET':
        response = await supabase
          .from(request.endpoint)
          .select()
          .match(request.params || {});
        break;
        
      case 'POST':
        response = await supabase
          .from(request.endpoint)
          .insert(request.body || {})
          .select();
        break;
        
      case 'PUT':
        response = await supabase
          .from(request.endpoint)
          .update(request.body || {})
          .match(request.params || {})
          .select();
        break;
        
      case 'DELETE':
        response = await supabase
          .from(request.endpoint)
          .delete()
          .match(request.params || {});
        break;
        
      default:
        throw new Error(`Unsupported method: ${request.method}`);
    }
    
    if (response.error) {
      const apiError = normalizeApiError(response.error);
      logApiError(apiError, { request });
      throw apiError;
    }
    
    return {
      data: response.data as TResponse,
      error: null,
      status: response.status
    };
  } catch (error) {
    console.error(`API error (${request.method} ${request.endpoint}):`, error);
    
    // Normalize and log error
    const apiError = normalizeApiError(error);
    logApiError(apiError, { request });
    
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Unknown API error'),
      status: apiError.status || 500
    };
  }
}; 